"use client";

import { useState } from "react";
import { useAuth } from "@clerk/nextjs";
import Link from "next/link";
import {
  ChevronDown,
  ChevronRight,
  Download,
  FileText,
  History,
  MessageSquare,
  BookOpen,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  getPdfVersionDetailHref,
  getLatestPdfVersionInfo,
  getPdfVersions,
  isNumericPdfVersion,
  versionBaseLabel,
  type VersionInfo,
} from "@/lib/pdf-version-data";
import { getTokenWithRetry } from "@/lib/utils";
import { newCommunityIssueHref } from "@/lib/community-issues";

export {
  comparePdfVersions,
  getLatestPdfVersionDate,
  getLatestPdfVersionInfo,
  hasPdfVersionAtLeast,
} from "@/lib/pdf-version-data";

interface PdfVersionProps {
  id: string;
  defaultExpanded?: boolean;
  showDownloadButton?: boolean;
  orderId?: string;
  /**
   * 목록 하단에 "이 요약본 질문·오류 제보"(이슈 게시판 새 글, 상품 프리셋)와
   * "전체 변경 이력"(/summary-pdf/changelog) 링크를 노출한다 (JCG-3029). 기본 true.
   */
  showIssueLinks?: boolean;
}

export default function PdfVersion({
  id,
  defaultExpanded = false,
  showDownloadButton = false,
  orderId,
  showIssueLinks = true,
}: PdfVersionProps) {
  const { getToken } = useAuth();
  const versions = getPdfVersions(id);
  const latestVersion = getLatestPdfVersionInfo(id)?.version ?? null;
  const [expandedVersions, setExpandedVersions] = useState<Set<number>>(
    new Set(defaultExpanded ? [0] : []),
  );

  const toggleVersion = (index: number) => {
    setExpandedVersions((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const handleDownload = async (version: VersionInfo) => {
    if (!orderId) return;

    // 세션 토큰 확인 (JCG-1315)
    // 일시적 토큰 실패로 정상 사용자가 리다이렉트되는 것을 방지하기 위해 재시도 적용
    const token = await getTokenWithRetry(getToken);
    if (!token) {
      alert("로그인이 필요합니다. 로그인 페이지로 이동합니다.");
      window.location.assign(
        `/sign-in?redirect_url=${encodeURIComponent(window.location.pathname)}`,
      );
      return;
    }

    const downloadUrl = `/api/download-pdf?orderId=${orderId}&version=${version.version}`;

    // iOS Safari: blob URL + link.click() 방식이 동작하지 않으므로
    // 직접 URL 이동으로 다운로드 (쿠키 인증 + Content-Disposition: attachment)
    // iOS Chrome(CriOS), Firefox(FxiOS) 등은 정상 동작하므로 Safari만 대상
    const ua = navigator.userAgent;
    const isIOSDevice =
      /iPad|iPhone|iPod/.test(ua) ||
      (ua.includes("Macintosh") && "ontouchend" in document);
    const isIOSSafari = isIOSDevice && !/CriOS|FxiOS|OPiOS|EdgiOS/.test(ua);
    if (isIOSSafari) {
      window.location.assign(downloadUrl);
      return;
    }

    try {
      // 인앱 브라우저에서 쿠키가 격리될 수 있으므로
      // getToken()으로 JWT를 가져와 Authorization 헤더로 전달
      const response = await fetch(downloadUrl, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (!response.ok) {
        if (response.status === 401) {
          alert("로그인이 필요합니다. 로그인 페이지로 이동합니다.");
          window.location.assign(
            `/sign-in?redirect_url=${encodeURIComponent(window.location.pathname)}`,
          );
          return;
        }
        const error = await response.json();
        alert(`다운로드 실패: ${error.error}`);
        return;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const contentDisposition = response.headers.get("content-disposition");
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(
          /filename\*=UTF-8''(.+)/,
        );
        if (fileNameMatch) {
          link.download = decodeURIComponent(fileNameMatch[1]);
        } else {
          link.download =
            version.blobPath.split("/").pop() ||
            `${id}_v${version.version}.pdf`;
        }
      } else {
        link.download =
          version.blobPath.split("/").pop() || `${id}_v${version.version}.pdf`;
      }
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("다운로드 오류:", error);
      alert("다운로드 중 오류가 발생했습니다.");
    }
  };

  if (versions.length === 0) {
    return null;
  }

  return (
    <div className="space-y-2">
      {versions.map((version, index) => (
        <div
          key={`${version.version}-${version.date}`}
          className="rounded-lg border bg-muted/30 p-3"
        >
          <div className="flex items-start justify-between gap-2">
            <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5">
              <span
                className={`inline-flex shrink-0 items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium ${
                  latestVersion !== null &&
                  versionBaseLabel(version.version) === latestVersion
                    ? "bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-400"
                    : "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                }`}
              >
                v{version.version}
              </span>
              <span className="whitespace-nowrap text-sm text-muted-foreground">
                ({version.date})
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => toggleVersion(index)}
              className="-mr-1 -mt-1 size-8 touch-manipulation p-0"
              aria-expanded={expandedVersions.has(index)}
              aria-label={`v${version.version} 변경 내용 ${
                expandedVersions.has(index) ? "접기" : "펼치기"
              }`}
            >
              {expandedVersions.has(index) ? (
                <ChevronDown className="h-4 w-4" aria-hidden />
              ) : (
                <ChevronRight className="h-4 w-4" aria-hidden />
              )}
            </Button>
          </div>

          {expandedVersions.has(index) && (
            <div className="mt-2 border-t pt-2">
              <p className="mb-3 text-sm text-foreground">
                {version.description}
              </p>
              {showDownloadButton && orderId && (
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    size="sm"
                    onClick={() => handleDownload(version)}
                    aria-label={`v${version.version} PDF 다운로드`}
                    className="touch-manipulation appearance-none [-webkit-appearance:none]"
                  >
                    <Download className="size-3" aria-hidden />
                    <span>다운로드</span>
                  </Button>
                  <Button asChild variant="outline" size="sm" className="touch-manipulation">
                    <Link
                      href={`/my/purchases/viewer/${encodeURIComponent(id)}/${encodeURIComponent(version.version)}`}
                      aria-label={`v${version.version} PDF 웹에서 보기`}
                    >
                      <BookOpen className="size-3" aria-hidden />
                      <span>웹에서 보기</span>
                    </Link>
                  </Button>
                  {isNumericPdfVersion(version.version) &&
                    version.detailAvailable === true && (
                      <Button
                        asChild
                        variant="outline"
                        size="sm"
                        className="touch-manipulation"
                      >
                        <Link
                          href={getPdfVersionDetailHref(id, version.version)}
                          aria-label={`v${version.version} 변경 상세 보기`}
                        >
                          <FileText className="size-3" aria-hidden />
                          <span>자세히</span>
                        </Link>
                      </Button>
                    )}
                </div>
              )}
            </div>
          )}
        </div>
      ))}
      {showIssueLinks && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-1 pt-1 text-xs text-muted-foreground">
          <Link
            href={newCommunityIssueHref({ category: "summary_pdf", productId: id })}
            className="inline-flex items-center gap-1 underline-offset-2 hover:text-foreground hover:underline"
          >
            <MessageSquare className="size-3.5" aria-hidden />
            이 요약본 질문·오류 제보
          </Link>
          <Link
            href="/summary-pdf/changelog"
            className="inline-flex items-center gap-1 underline-offset-2 hover:text-foreground hover:underline"
          >
            <History className="size-3.5" aria-hidden />
            전체 변경 이력
          </Link>
        </div>
      )}
    </div>
  );
}
